import {useCallback, useEffect, useState} from "react";
import {ResponseTimeoutState} from "../types/ResponseTimeoutState";
import {useDispatch} from "react-redux";

interface UseResponseTimeoutOptions {
  timeout: number;
  responseTimeoutState: ResponseTimeoutState;
  onTimeout: () => void;
}

/**
 * Hook for managing response timeout
 * Starts a timeout timer and clears it when recognition state changes or on unmount
 */
export function useResponseTimeout({timeout, responseTimeoutState, onTimeout}: UseResponseTimeoutOptions) {
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const dispatch = useDispatch();
  const setResponseState = useCallback(
    (state: ResponseTimeoutState) =>
      dispatch({
        type: "update",
        payload: {responseTimeoutState: state},
      }),
    [dispatch],
  );

  const startTimeout = useCallback(() => {
    setResponseState(ResponseTimeoutState.STARTED);
    const id = window.setTimeout(() => {
      onTimeout();
      setTimeoutId(null);
      setResponseState(ResponseTimeoutState.FINISHED);
    }, timeout);
    setTimeoutId(id);
  }, [timeout, onTimeout, setResponseState]);

  const clearActiveTimeout = useCallback(() => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [timeoutId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearActiveTimeout();
    };
  }, [timeoutId, clearActiveTimeout]);

  // Clear timeout when response timeout state = ABORTING
  useEffect(() => {
    if (responseTimeoutState === ResponseTimeoutState.ABORTING) {
      clearActiveTimeout();
      setResponseState(ResponseTimeoutState.ABORTED);
    }
  }, [responseTimeoutState, clearActiveTimeout, setResponseState]);

  return {
    startTimeout,
    clearTimeout: clearActiveTimeout,
    isActive: timeoutId !== null,
  };
}
